import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { SubpageHeader } from "@/components/birthlife/SubpageHeader";
import { Footer } from "@/components/birthlife/Footer";
import { Breadcrumbs } from "@/components/birthlife/Breadcrumbs";
import { Seo, breadcrumbLd, faqPageLd, SITE_URL } from "@/lib/seo";
import { useSite } from "@/lib/site";
import NotFound from "@/pages/NotFound";

const webPageLd = (page) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${SITE_URL}/${page.slug}/`,
  name: page.h1,
  description: page.meta_desc || "",
  url: `${SITE_URL}/${page.slug}/`,
  isPartOf: { "@id": `${SITE_URL}/#business` },
  about: { "@id": `${SITE_URL}/${page.slug === "about-yogyata-wadhwa" ? "#yogyata" : "#business"}` },
});

export default function LandingPage() {
  const { lpSlug } = useParams();
  const { content } = useSite();
  const pages = content.landing_pages;
  const page = (pages || []).find((p) => p.slug === lpSlug && p.published !== false);

  if (pages && !page) return <NotFound />;
  if (!page) return null;

  const crumbs = [{ label: page.h1, href: `/${page.slug}/` }];

  return (
    <div data-testid="landing-page" className="min-h-screen bg-sand">
      <Seo
        title={page.title}
        description={page.meta_desc}
        path={`/${page.slug}/`}
        jsonLd={[
          webPageLd(page),
          breadcrumbLd(crumbs),
          ...((page.faqs || []).length ? [faqPageLd(page.faqs.map((f) => ({ question: f.q, answer: f.a })))] : []),
        ]}
      />
      <SubpageHeader />

      <main className="mx-auto max-w-3xl px-4 pb-16 sm:px-6 lg:px-8">
        <Breadcrumbs items={crumbs} />

        <p data-testid="landing-eyebrow" className="mt-8 font-outfit text-xs font-semibold uppercase tracking-[0.28em] text-terra">
          {page.eyebrow}
        </p>
        <h1 data-testid="landing-h1" className="mt-4 font-display text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl">
          {page.h1}
        </h1>
        <p className="mt-5 text-base leading-relaxed text-plumsoft sm:text-lg">{page.intro}</p>

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            data-testid="landing-book-cta"
            href="/#services"
            className="group inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 font-outfit text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
          >
            Book a consultation
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <Link
            data-testid="landing-courses-cta"
            to="/#courses"
            className="inline-flex items-center rounded-full border border-ink/15 bg-white px-7 py-3.5 font-outfit text-sm font-semibold text-ink transition-colors duration-300 hover:border-terra hover:text-terra"
          >
            Explore courses
          </Link>
        </div>

        {(page.sections || []).map((sec, i) => (
          <section key={i} data-testid={`landing-section-${i}`} className="mt-12">
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl">{sec.heading}</h2>
            {(sec.body || "").split("\n\n").filter(Boolean).map((para, j) => (
              <p key={j} className="mt-3 whitespace-pre-line text-base leading-relaxed text-plumsoft">
                {para}
              </p>
            ))}
          </section>
        ))}

        {(page.faqs || []).length > 0 && (
          <section className="mt-12" data-testid="landing-faqs">
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl">Frequently asked questions</h2>
            <div className="mt-5 space-y-3">
              {page.faqs.map((f, i) => (
                <details
                  key={i}
                  data-testid={`landing-faq-${i}`}
                  className="group rounded-2xl border border-ink/10 bg-white px-6 py-4 open:border-terra/30"
                >
                  <summary className="cursor-pointer list-none font-outfit text-[15px] font-semibold text-ink marker:hidden">
                    {f.q}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-plumsoft">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 flex items-start gap-3 rounded-2xl border border-terra/20 bg-terra/5 p-6">
          <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-terra" />
          <p className="text-sm leading-relaxed text-plumsoft">
            <strong className="text-ink">The Birth Life</strong> — 419, Fourth Floor, Eros City Square, Sector 49,
            Gurugram, Haryana 122018. No home visits — in-clinic in Sector 49, Gurgaon and online worldwide.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
